import React, { useState } from "react";
import {
  DEPOSIT_EXPLAINER,
  MOVE_OUT_TIPS,
  PRICES_DISCLAIMER,
} from "../renewalCopy";
import { Card } from "../renewalUi";

/**
 * "Tips for Avoiding Fees at Move-Out" — expandable price sections plus the
 * deposit explainer. Rendered inline on the move-out Condition step and
 * linked from both confirmation screens.
 *
 * Per the updates doc the expander is deliberately prominent, and every
 * expanded section closes with the red "examples only" disclaimer.
 */
const MoveOutTips: React.FC<{ defaultOpen?: boolean }> = ({
  defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [openSection, setOpenSection] = useState<string | null>(null);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full cursor-pointer items-center justify-between gap-3 rounded-lg border border-primary/20 bg-primary/5 px-4 py-2.5 text-left transition-colors hover:bg-primary/10"
      >
        <span>
          <span className="block text-sm font-semibold text-primary">
            Tips for Avoiding Fees at Move-Out
          </span>
          <span className="mt-0.5 block text-xs text-gray-600">
            See common charges and how to prevent them
          </span>
        </span>
        <svg
          className={`h-5 w-5 shrink-0 text-primary transition-transform ${
            open ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="mt-3 space-y-4">
          <p className="text-sm leading-relaxed text-gray-600">
            {MOVE_OUT_TIPS.intro}
          </p>

          {/* One card holding all four categories as a divided list — a card
              per category made each row a tall band around a single line. */}
          <div className="overflow-hidden rounded-xl bg-white shadow-sm">
            {MOVE_OUT_TIPS.sections.map((section, idx) => {
              const isOpen = openSection === section.title;
              return (
                <div
                  key={section.title}
                  className={idx > 0 ? "border-t border-gray-100" : ""}
                >
                  <button
                    type="button"
                    onClick={() => setOpenSection(isOpen ? null : section.title)}
                    className="flex w-full cursor-pointer items-center justify-between gap-4 px-4 py-2.5 text-left transition-colors hover:bg-gray-50"
                  >
                    <span className="text-sm font-semibold text-primary">
                      {section.title}
                    </span>
                    <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-info">
                      {isOpen ? "Hide" : "See prices"}
                      <svg
                        className={`h-3.5 w-3.5 transition-transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </span>
                  </button>

                  {isOpen && (
                    <div className="bg-gray-50/60 px-4 pb-3 pt-1">
                      <dl className="divide-y divide-gray-200/70">
                        {section.items.map((item) => (
                          <div
                            key={item.label}
                            className="flex items-baseline justify-between gap-6 py-1.5"
                          >
                            <dt className="text-sm text-gray-600">
                              {item.label}
                            </dt>
                            <dd className="whitespace-nowrap text-sm font-semibold tabular-nums text-primary">
                              {item.value}
                            </dd>
                          </div>
                        ))}
                      </dl>
                      {/* Disclaimer stays with the prices it qualifies —
                          the tip below is separate advice. */}
                      <p className="mt-2 text-xs text-danger">
                        {PRICES_DISCLAIMER}
                      </p>
                      {/* Same treatment as the pro tip in the "How SDAs
                          work" card below. */}
                      <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm leading-relaxed text-amber-900">
                        💡 <strong className="font-semibold">TIP:</strong>{" "}
                        {section.tip}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <Card>
            <h4 className="mb-3 text-sm font-semibold text-primary">
              {DEPOSIT_EXPLAINER.title}
            </h4>
            <div className="space-y-2.5">
              {DEPOSIT_EXPLAINER.paragraphs.map((p, i) => (
                <p key={i} className="text-sm leading-relaxed text-gray-600">
                  {p}
                </p>
              ))}
            </div>
            <p className="mt-4 rounded-lg bg-amber-50 px-4 py-3 text-sm leading-relaxed text-amber-900">
              💡{" "}
              <strong className="font-semibold">
                {DEPOSIT_EXPLAINER.proTipLead}
              </strong>{" "}
              {DEPOSIT_EXPLAINER.proTip}
            </p>
          </Card>
        </div>
      )}
    </div>
  );
};

export default MoveOutTips;
